import { observable, computed } from 'mobx';
import MapView from '@arcgis/core/views/MapView';
import * as MapViewInputOperations from './MapViewInputOperations';

import { MeasureFeedbackHandler } from './MapViewInputOperations';
import { SketchInputResult, SketchInputType } from './SketchInputResult';
import { MeasureInputResult, MeasureInputType } from './MeasureInputResult';
import { TextSketchInputProperties } from './TextSketchInputProperties';
import { CancelTracker } from './CancelTracker';
import { ISelectedMeasurementUnits } from 'cd-eft/components/common/MeasureTools/MeasureToolboxView';

export type MapViewInputOperationType = SketchInputType | MeasureInputType;

export class MapViewInputManager {
  //public readonly mapView: MapView;
  public mapView: MapView;

  @observable private _cancelTracker?: CancelTracker;
  @observable private _currentOperationType?: MapViewInputOperationType;
  @observable private _currentOperationHint?: string;

  constructor(mapView: MapView) {
    this.mapView = mapView;
  }

  public async cancelCurrentOperationAsync(): Promise<void> {
    if (this._cancelTracker) {
      await this._cancelTracker.cancelAsync();
      this._cancelTracker = undefined;
    }
    return;
  }

  @computed public get canCancel(): boolean {
    return this._cancelTracker !== undefined;
  }

  @computed public get currentOperationType(): MapViewInputOperationType | undefined {
    return this._currentOperationType;
  }

  @computed public get currentOperationHint(): string | undefined {
    return this._currentOperationHint;
  }

  private async measureAsync(
    selectedUnit: ISelectedMeasurementUnits,
    type: 'measure-area' | 'measure-distance',
    measureFeedbackHandler?: MeasureFeedbackHandler
  ) {
    if (this._cancelTracker) {
      await this._cancelTracker.cancelAsync();
      this._cancelTracker = undefined;
    }
    this._cancelTracker = new CancelTracker();
    try {
      switch (type) {
        case 'measure-area':
          this._currentOperationType = type;
          this._currentOperationHint = 'Click points in the map to draw a polygon for area measurement.';
          break;
        case 'measure-distance':
          this._currentOperationType = type;
          this._currentOperationHint = 'Click points in the map to draw a polyline for distance measurement.';
          break;
      }

      return await MapViewInputOperations.measureAsync(
        this.mapView,
        type,
        selectedUnit,
        this._cancelTracker,
        measureFeedbackHandler
      );
    } finally {
      this._currentOperationType = undefined;
      this._currentOperationHint = undefined;
      this._cancelTracker = undefined;
    }
  }

  public async measureDistanceAsync(
    selectedUnit: ISelectedMeasurementUnits,
    measureFeedbackHandler?: MeasureFeedbackHandler
  ): Promise<MeasureInputResult> {
    return await this.measureAsync(selectedUnit, 'measure-distance', measureFeedbackHandler);
  }

  public async measureAreaAsync(
    selectedUnits: ISelectedMeasurementUnits,
    measureFeedbackHandler?: MeasureFeedbackHandler
  ): Promise<MeasureInputResult> {
    return await this.measureAsync(selectedUnits, 'measure-area', measureFeedbackHandler);
  }

  private async sketchAsync(type: SketchInputType, textSketchInputProps?: TextSketchInputProperties) {
    if (this._cancelTracker) {
      await this._cancelTracker.cancelAsync();
      this._cancelTracker = undefined;
    }
    this._cancelTracker = new CancelTracker();
    try {
      switch (type) {
        case 'sketch-point':
          this._currentOperationType = 'sketch-point';
          this._currentOperationHint = 'Click in the map to define a point.';
          break;
        case 'sketch-multipoint':
          this._currentOperationType = 'sketch-multipoint';
          this._currentOperationHint = 'Click one (1) or more points in the map to define a multi-point.';
          break;
        case 'sketch-polyline':
          this._currentOperationType = 'sketch-polyline';
          this._currentOperationHint = 'Click two (2) or more points in the map to define a polyline.';
          break;
        case 'sketch-polygon':
          this._currentOperationType = 'sketch-polygon';
          this._currentOperationHint = 'Click three (3) or more points in the map to define a polygon.';
          break;
        case 'sketch-rectangle':
          this._currentOperationType = 'sketch-rectangle';
          this._currentOperationHint = 'Click, drag, and release in the map to define a rectangle.';
          break;
        case 'sketch-circle':
          this._currentOperationType = 'sketch-circle';
          this._currentOperationHint = 'Click, drag, and release in the map to define a circle.';
          break;
        case 'sketch-text':
          this._currentOperationType = 'sketch-text';
          this._currentOperationHint = 'Click in the map to place the text.';
          break;
        default:
          throw new Error('unexpected input: type');
      }
      return await MapViewInputOperations.sketchAsync(this.mapView, type, this._cancelTracker, textSketchInputProps);
    } finally {
      this._currentOperationType = undefined;
      this._currentOperationHint = undefined;
      this._cancelTracker = undefined;
    }
  }

  async sketchPointAsync(): Promise<SketchInputResult> {
    return (await this.sketchAsync('sketch-point')) as SketchInputResult;
  }

  async sketchMultipointAsync(): Promise<SketchInputResult> {
    return (await this.sketchAsync('sketch-multipoint')) as SketchInputResult;
  }

  async sketchPolylineAsync(): Promise<SketchInputResult> {
    return (await this.sketchAsync('sketch-polyline')) as SketchInputResult;
  }

  async sketchPolygonAsync(): Promise<SketchInputResult> {
    return (await this.sketchAsync('sketch-polygon')) as SketchInputResult;
  }

  async sketchRectangleAsync(): Promise<SketchInputResult> {
    return (await this.sketchAsync('sketch-rectangle')) as SketchInputResult;
  }

  async sketchCircleAsync(): Promise<SketchInputResult> {
    return (await this.sketchAsync('sketch-circle')) as SketchInputResult;
  }

  async sketchTextAsync(textSketchInputProps: TextSketchInputProperties): Promise<SketchInputResult> {
    return (await this.sketchAsync('sketch-text', textSketchInputProps)) as SketchInputResult;
  }

  async getMapViewImmediateClick(
    cursor: 'default' | 'crosshair' | 'pointer',
    pointerMoveHandler?: (e: any) => void
  ): Promise<MapViewInputOperations.IMapViewClickEventData> {
    if (this._cancelTracker) {
      await this._cancelTracker.cancelAsync();
      this._cancelTracker = undefined;
    }

    const surfaceElement: HTMLElement = (this.mapView as any).surface;
    const previousCursor = surfaceElement.dataset.cursor;

    try {
      this._cancelTracker = new CancelTracker();
      this._currentOperationType = undefined;
      this._currentOperationHint = undefined;

      surfaceElement.dataset.cursor = cursor;

      return await MapViewInputOperations.getMapViewGesture(
        this.mapView,
        'immediate-click',
        this._cancelTracker,
        pointerMoveHandler
      );
    } finally {
      this._currentOperationType = undefined;
      this._currentOperationHint = undefined;
      this._cancelTracker = undefined;

      surfaceElement.dataset.cursor = previousCursor;
    }
  }
}
